<template>
  <div class="extended-filters">
    <div class="row-f calories">
      <div class="filter">
        <span>Kcal od (min)</span>
        <BaseInputNumber
          test-id="IngredientsExtendedFilters__KcalMin"
          class="filter-number"
          :min="kcalMin"
          :max="kcalMax"
          :precision="1"
          :step="0.1"
          :model-value="kcalMinValue"
          @update:model-value="setKcalMin"
        />
      </div>
      <div class="filter">
        <span>Kcal do (max)</span>
        <BaseInputNumber
          test-id="IngredientsExtendedFilters__KcalMax"
          class="filter-number"
          :min="kcalMin"
          :max="kcalMax"
          :precision="1"
          :step="0.1"
          :model-value="kcalMaxValue"
          @update:model-value="setKcalMax"
        />
      </div>
    </div>
    <div class="row-f">
      <div class="filter-box category-box">
        <span>Rodzaj produktu</span>
        <BaseCheckbox
          v-for="category in categoryChoices"
          :key="category.value"
          :label="category.text"
          :class="{ disabled: category.disabled }"
          :model-value="category.active"
          @update:model-value="setIngredientType(category.value)"
        />
      </div>
      <div class="filter-box base-box">
        <span>Baza produktów</span>
        <div
          v-for="ingredientBase in ingredientBasesChoices"
          :key="ingredientBase.value"
          class="ingredient-base-row"
        >
          <BaseCheckbox
            :label="ingredientBase.text"
            :class="{ disabled: ingredientBase.disabled }"
            :model-value="ingredientBase.active"
            @update:model-value="setIngredientBase(ingredientBase.value)"
          />
        </div>
      </div>
      <div class="filter-box ingredient-category-box">
        <span>Kategoria</span>
        <div class="filter-box-wrapped">
          <BaseCheckbox
            v-for="ingredientCategory in ingredientCategoriesChoices"
            :key="ingredientCategory.value"
            :label="ingredientCategory.text"
            :model-value="ingredientCategory.active"
            @update:model-value="setIngredientCategory(ingredientCategory.value)"
          />
        </div>
      </div>
      <div class="filter-box tag-box">
        <span>Tagi systemowe</span>
        <BaseCheckbox
          v-for="ingredientTag in ingredientTagsChoices"
          :key="ingredientTag.value"
          :label="ingredientTag.text"
          :model-value="ingredientTag.active"
          @update:model-value="setIngredientTag(ingredientTag.value)"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { A, D } from '@mobily/ts-belt'
  import { computed, onBeforeMount, ref, watch } from 'vue'

  import BaseCheckbox from '@/components/BaseCheckbox.vue'
  import BaseInputNumber from '@/components/BaseInputNumber.vue'
  import { useIngredientsProvider } from '@/types/IngredientList'
  import { ingredientBases, ingredientCategories, ingredientTags } from '@/utils/common'

  import type { IngredientType } from '@/types/IngredientList'

  type List = {
    text: string
    value: number
  }
  export type CategoryChoice = {
    active: boolean
    disabled: boolean
    text: string
    value: IngredientType
  }
  export type Choice = {
    active: boolean
    text: string
    value: string
  }

  export type IngredientBaseChoice = {
    active: boolean
    disabled: boolean
    text: string
    value: number
  }

  const {
    kcalMax,
    kcalMin,
    ingredientsType,
    resetFiltersValue,
    setIngredientCategoryForFilters: _setIngredientCategoryForFilters,
    setIngredientBaseForFilters: _setIngredientBaseForFilters,
    setIngredientTagForFilters: _setIngredientTagForFilters,
    setIngredientTypeForFilters: _setIngredientTypeForFilters,
    setKcalMin: _setKcalMin,
    setKcalMax: _setKcalMax,
    resetKcalMinMax,
  } = useIngredientsProvider()

  const categoryValues = ref<IngredientType[]>([])
  const ingredientTagsValues = ref<string[]>([])
  const ingredientBasesValues = ref<number[]>([])
  const ingredientCategoriesValues = ref<number[]>([])
  const ingredientBasesList = ref<List[]>([])
  const kcalMinValue = ref(0)
  const kcalMaxValue = ref(9999)

  const categoryChoices = computed<CategoryChoice[]>(() => {
    const choices: CategoryChoice[] = [
      {
        active: false,
        disabled: ingredientsType.value === 'is_private' || ingredientsType.value === 'is_public',
        text: 'Publiczne',
        value: 'is_public',
      },
      {
        active: false,
        disabled: ingredientsType.value === 'is_public' || ingredientsType.value === 'is_private',
        text: 'Prywatne',
        value: 'is_private',
      },
      {
        active: false,
        disabled: ingredientsType.value === 'is_favorite',
        text: 'Ulubione',
        value: 'is_favorite',
      },
    ]
    return choices.map(choice => {
      choice.active =
        categoryValues.value.includes(choice.value) || choice.value === ingredientsType.value
      return choice
    })
  })

  const ingredientTagsChoices = computed(() => {
    const choices: Choice[] = []
    A.forEach(D.keys(ingredientTags), tag => {
      choices.push({
        active: ingredientTagsValues.value.includes(tag),
        text: ingredientTags[tag],
        value: tag,
      })
    })

    return choices
  })

  const ingredientCategoriesChoices = computed(() => {
    return ingredientCategories.map(choice => {
      const active = ingredientCategoriesValues.value.includes(choice.value)
      return {
        active,
        text: choice.text,
        value: choice.value,
      }
    })
  })

  const ingredientBasesChoices = computed<IngredientBaseChoice[]>(() => {
    return ingredientBases.map(choice => {
      const active = ingredientBasesValues.value.includes(choice.value)
      return {
        active,
        disabled: false,
        text: choice.text,
        value: choice.value,
      }
    })
  })

  const setKcalMin = (value: number) => {
    kcalMinValue.value = value
    _setKcalMin(value)
  }
  const setKcalMax = (value: number) => {
    kcalMaxValue.value = value
    _setKcalMax(value)
  }
  const addOrRemoveFromList = <T>(valuesList: T[], value: T) => {
    const index = valuesList.indexOf(value)
    if (index === -1) {
      valuesList.push(value)
    } else {
      valuesList.splice(index, 1)
    }
  }
  const setIngredientTag = (value: string) => {
    addOrRemoveFromList(ingredientTagsValues.value, value)
    _setIngredientTagForFilters(ingredientTagsValues.value)
  }
  const setIngredientType = (value: string) => {
    addOrRemoveFromList(categoryValues.value, value)
    _setIngredientTypeForFilters(categoryValues.value)
  }
  const setIngredientCategory = (value: number) => {
    addOrRemoveFromList(ingredientCategoriesValues.value, value)
    _setIngredientCategoryForFilters(ingredientCategoriesValues.value)
  }
  const setIngredientBase = (value: number) => {
    addOrRemoveFromList(ingredientBasesValues.value, value)
    _setIngredientBaseForFilters(ingredientBasesValues.value)
  }
  const resetExtendedFilters = () => {
    categoryValues.value = []
    ingredientTagsValues.value = []
    ingredientCategoriesValues.value = []
    ingredientBasesValues.value = []
    kcalMinValue.value = kcalMin.value
    kcalMaxValue.value = kcalMax.value
    resetKcalMinMax()
  }

  onBeforeMount(() => {
    kcalMaxValue.value = kcalMax.value
    kcalMinValue.value = kcalMin.value
    ingredientBasesList.value = ingredientBases
    ingredientBasesList.value.push({
      text: 'Kcalmar.pro',
      value: 7,
    })
  })
  watch(ingredientsType, () => {
    resetExtendedFilters()
  })
  watch(resetFiltersValue, () => {
    resetExtendedFilters()
  })
  watch(kcalMin, newValue => {
    kcalMinValue.value = newValue
  })
  watch(kcalMax, newValue => {
    kcalMaxValue.value = newValue
  })
</script>

<style scoped>
  .extended-filters {
    display: flex;
    flex-direction: column;
    margin-top: 23px;
    background: #fff;
  }

  .extended-filters .row-f.calories {
    column-gap: 16px;
    justify-content: flex-start;
    margin-bottom: 23px;
  }

  .extended-filters .row-f {
    display: flex;
    column-gap: 49px;
  }

  .extended-filters .filter-box {
    display: flex;
    flex-direction: column;
  }

  .extended-filters .filter-box .filter-box-wrapped {
    display: flex;
    flex-flow: column wrap;
    column-gap: 49px;
    justify-content: space-between;
    max-height: 250px;
  }

  .extended-filters .filter-box span,
  .extended-filters .filter span {
    margin-bottom: 16px;
    font-family: Montserrat-Medium;
    font-size: 14px;
  }

  .extended-filters .filter .base-input {
    width: 50px;
    text-align: center;
  }

  .extended-filters .filter-box .base-checkbox {
    margin-bottom: 19px;
  }

  .category-box,
  .tag-box,
  .base-box {
    width: 20%;
  }

  .ingredient-category-box {
    width: 40%;
  }

  .ingredient-base-row {
    display: flex;
  }

  .ingredient-base-row .unlock {
    box-sizing: border-box;
    height: 15px;
    padding: 2px;
    margin-left: 8px;
    font-family: Montserrat-SemiBold;
    font-size: 8px;
    color: var(--color-destructive-100);
    cursor: pointer;
    border: 1px solid var(--color-destructive-100);
    border-radius: 4px;
  }
</style>
